<script lang="ts" setup>
/* eslint-disable vue/no-v-html */
export type FAQItem = {
    question: string
    answer: string
}
export interface WidgetFaqProps {
    data: {
        data: FAQItem[]
        title: string
        subtitle: string
        tag: string
    }
    rating?: {
        rating: number
        title?: string
    }
    headingLevel: HeadingLevel
    collapsable?: boolean
    initialCount?: number
    // Optional override for heading to avoid duplicate headings (WCAG 2.4.6)
    headingOverride?: string
}

const props = withDefaults(defineProps<WidgetFaqProps>(), {
    rating: undefined,
    collapsable: false,
    initialCount: 5,
    headingOverride: undefined,
})

const { $t } = useNuxtApp()

// Use override if provided, otherwise fall back to data title
const displayHeading = computed(() => props.data.title)

const showAll = ref<boolean>(props.initialCount >= props.data.data.length)
const accordionRef = ref<HTMLElement>()

// Accessibility: Screen reader announcement for showing more content (WCAG 4.1.2)
const showMoreAnnouncement = ref<string>('')

const formattedData = computed<FAQItem[]>((): FAQItem[] => {
    const data = props.collapsable && !showAll.value
        ? props.data.data.slice(0, props.initialCount)
        : props.data.data

    // Sanitize HTML content for accessibility (WCAG 4.1.2)
    return data.map((item) => ({
        ...item,
        answer: useSanitizeHtml(item.answer)
    }))
})

// Focus management for WCAG 2.4.3 - move focus to first new item after expanding
const handleShowMore = () => {
    const firstNewIndex = props.initialCount
    const newItemsCount = props.data.data.length - props.initialCount
    showAll.value = true

    // Announce new content to screen readers (WCAG 4.1.2)
    showMoreAnnouncement.value = ''
    nextTick(() => {
        showMoreAnnouncement.value = `${$t('Zobrazených')} ${newItemsCount} ${$t('ďalších otázok')}`

        if (accordionRef.value) {
            // Focus the first newly revealed accordion summary
            const accordionItems = accordionRef.value.querySelectorAll<HTMLDetailsElement>('details')
            const firstNewItem = accordionItems[firstNewIndex]
            if (firstNewItem) {
                const summary = firstNewItem.querySelector('summary')
                if (summary) {
                    summary.focus()
                }
            }
        }
    })
}
</script>
<template>
  <section data-cy="widget_faq">
    <Tracker :data="{ position: displayHeading }">
      <BoxHeader
        :heading-level="props.headingLevel"
        :heading="displayHeading"
        :subtitle="props.data.subtitle"
      >
        <template
          v-if="props.data.tag"
          #tag
        >
          <Tag
            size="small"
            background-color="gray-1"
            class="text-uppercase"
          >
            {{ props.data.tag }}
          </Tag>
        </template>
      </BoxHeader>
      <div
        class="row justify-content-center"
        :class="{
          'justify-content-lg-between':
            props.data.data.length && props.rating,
        }"
      >
        <div
          v-if="props.data.data.length"
          class="col-12 col-lg-8 mb-12 mb-lg-0"
        >
          <Accordion ref="accordionRef">
            <AccordionItem
              v-for="(item, index) in formattedData"
              :key="`faqi-${index}`"
              :btn-index="index"
              :btn-text="item.question"
            >
              <Typo class="mx-4 mx-md-6 mb-4 mb-md-6">
                <div v-html="item.answer" />
              </Typo>
            </AccordionItem>
          </Accordion>
          <!-- Screen reader announcement for showing more content (WCAG 4.1.2) -->
          <div
            aria-live="polite"
            aria-atomic="true"
            class="visually-hidden"
          >
            {{ showMoreAnnouncement }}
          </div>
          <div
            class="d-flex justify-content-center justify-content-md-start m-4"
          >
            <VButton
              v-if="
                (props.collapsable && !showAll) ||
                  !props.collapsable
              "
              :to="props.collapsable ? null : { name: 'faq' }"
              color="link"
              type="button"
              class="d-flex align-items-center text-secondary"
              :aria-expanded="showAll.toString()"
              aria-controls="faq-content"
              @click="handleShowMore"
            >
              {{ $t('Zobraziť ďalšie otázky a odpovede') }}
              <svg
                class="d-block ms-1 text-secondary"
                aria-hidden="true"
                width="8"
                height="8"
              >
                <use
                  :xlink:href="`#${
                    props.collapsable
                      ? 'angle-down'
                      : 'angle-right'
                  }`"
                />
              </svg>
            </VButton>
          </div>
        </div>
        <div
          v-if="props.rating && props.rating.title"
          class="col-12 col-md-6 col-lg-4 col-xl-3"
        >
          <WidgetRating
            :rating="props.rating.rating"
            heading-level="h2"
            :heading="props.rating.title"
          />
        </div>
      </div>
    </Tracker>
  </section>
</template>
